import { DateTime, Duration } from 'luxon';

const INVALID = '—';

function fromAny(input: string | number | Date | DateTime): DateTime {
  if (input instanceof DateTime) return input;
  if (input instanceof Date) return DateTime.fromJSDate(input);
  if (typeof input === 'number') return DateTime.fromMillis(input);
  // Guard String(input): symbols throw and objects with a broken
  // Symbol.toPrimitive can too. A junk input becomes an invalid DateTime.
  try {
    return DateTime.fromISO(String(input));
  } catch {
    return DateTime.invalid('non-string input');
  }
}

/** "12 Jul 2026" */
export function fmtDate(input: string | number | Date): string {
  try {
    const dt = fromAny(input);
    return dt.isValid ? dt.toFormat('d MMM yyyy') : INVALID;
  } catch {
    return INVALID;
  }
}

/** "12 Jul 2026, 14:30" */
export function fmtDateTime(input: string | number | Date): string {
  try {
    const dt = fromAny(input);
    return dt.isValid ? dt.toFormat('d MMM yyyy, HH:mm') : INVALID;
  } catch {
    return INVALID;
  }
}

/** Auto: shows time if the input contains a time component, date-only otherwise. */
export function fmtDateAuto(input: string | number | Date): string {
  if (typeof input === 'string') {
    const hasTime = input.includes('T') || /\d{2}:\d{2}/.test(input);
    return hasTime ? fmtDateTime(input) : fmtDate(input);
  }
  return fmtDateTime(input);
}

/** "3 days ago", "2h ago", etc. */
export function fmtDateAgo(input: string | number | Date): string {
  try {
    const dt = fromAny(input);
    if (!dt.isValid) return INVALID;

    const diffMs = Date.now() - dt.toMillis();
    const diffS = Math.floor(diffMs / 1000);
    const diffM = Math.floor(diffMs / 60_000);
    const diffH = Math.floor(diffMs / 3_600_000);
    const diffD = Math.floor(diffMs / 86_400_000);

    if (diffD > 3) return fmtDate(input);
    if (diffD >= 1) return `${diffD}d ago`;
    if (diffH >= 1) return `${diffH}h ago`;
    if (diffM >= 1) return `${diffM}m ago`;
    return `${diffS}s ago`;
  } catch {
    return INVALID;
  }
}

/** "1h 24m 30s" — safe against Infinity / NaN */
export function fmtDuration(totalSeconds: number): string {
  // Unknown, not zero: a stream reports Infinity until it knows its length,
  // and "0s" would state a length nobody measured.
  if (!Number.isFinite(totalSeconds)) return '';
  const s = totalSeconds;
  const dur = Duration.fromObject({ seconds: s }).shiftTo(
    'hours',
    'minutes',
    'seconds'
  );
  const parts = [
    dur.hours > 0 && `${Math.floor(dur.hours)}h`,
    dur.minutes > 0 && `${Math.floor(dur.minutes)}m`,
    dur.seconds > 0 && `${Math.floor(dur.seconds)}s`
  ].filter(Boolean);
  return parts.length ? parts.join(' ') : '0s';
}
